import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut, ChevronDown, Activity, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMyOrganizations, useOrgSwitch } from '@/hooks/useOrgSwitch';
import { displayRole } from '@/lib/roleDisplay';
import { useEntitlements } from '@/hooks/useEntitlements';
import { CORP_PLATFORM_HEALTH_URL, surfaceForPath, visibleSurfaces } from '@/surfaces';
import type { MyOrganization } from '@/types';

/**
 * Surface switcher (JSS#1154): one tab per surface from the registry. The
 * tab opens the surface's first item; the sidebar lists the rest.
 */
function SurfaceSwitcher() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { entitlements } = useEntitlements();
  // Surfaces the acting org doesn't have are hidden (#1160).
  const surfaces = visibleSurfaces(user, entitlements?.surfaces);
  const active = surfaceForPath(surfaces, pathname);

  return (
    <nav aria-label="Surfaces" className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
      {surfaces.map((surface) => (
        <Link
          key={surface.id}
          to={surface.items[0].path}
          aria-current={active?.id === surface.id ? 'page' : undefined}
          className={clsx(
            'px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap',
            active?.id === surface.id
              ? 'bg-jeket-gold/20 text-jeket-gold'
              : 'text-jeket-silver-muted hover:text-jeket-silver-bright hover:bg-jeket-black-elevated'
          )}
        >
          {surface.label}
        </Link>
      ))}
    </nav>
  );
}

// Role badge colours, keyed by displayRole().key (#860).
const roleBadgeColors: Record<string, string> = {
  superuser: 'bg-jeket-gold-dark',
  admin: 'bg-red-500/80',
  engineer: 'bg-jeket-info/80',
  'site-operator': 'bg-jeket-info/60',
  'event-operator': 'bg-jeket-silver-muted/50',
  member: 'bg-jeket-silver-muted/50',
};

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { selectedOrgId, switchOrg } = useOrgSwitch();

  // The orgs this user may act in (#847): their own, or every org for a
  // superuser. It used to be every org from /api/admin, for any `admin` —
  // now superuser-only, and the BFF refuses a non-member's pick anyway.
  const isSuperuser = user?.role === 'superuser';
  const { data: organizations } = useMyOrganizations(!!user);

  const handleOrgSwitch = (orgId: string | null) => {
    setUserMenuOpen(false);
    switchOrg(orgId);
  };

  // A saved pick outside the user's orgs (made back when any admin could
  // switch into any org) gets a 403 on every org-scoped call. Clear it.
  useEffect(() => {
    if (!organizations || isSuperuser || !selectedOrgId) return;
    if (!organizations.some((o) => o.id === selectedOrgId)) handleOrgSwitch(null);
  }, [organizations, isSuperuser, selectedOrgId]);

  // Superusers may act in any org or none ("All Organizations"); a member
  // of several orgs picks one of theirs; a single-org member has no choice.
  const showOrgSwitcher = !!organizations && (isSuperuser ? organizations.length > 0 : organizations.length > 1);

  const selectedOrg = organizations?.find((o: MyOrganization) => o.id === selectedOrgId);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 h-14 bg-jeket-black-surface border-b border-jeket-gold/20 flex items-center px-4 gap-6">
      {/* Logo */}
      <div className="flex items-center gap-2">
        <img src="/logo.png" alt="Jeket" className="h-7" />
      </div>

      <SurfaceSwitcher />

      {/* Right side: User */}
      <div className="flex items-center gap-2">

        {/* User menu */}
        {user && (
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-jeket-black-elevated transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-jeket-black-elevated border border-jeket-gold/30 flex items-center justify-center">
                <User size={14} className="text-jeket-gold" />
              </div>
              {selectedOrg && (
                <span className="text-xs text-jeket-gold font-medium max-w-[100px] truncate">
                  {selectedOrg.name}
                </span>
              )}
              <ChevronDown size={14} className="text-jeket-silver-muted" />
            </button>

            {/* Dropdown menu */}
            {userMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setUserMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 w-56 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg shadow-jeket z-20 py-1">
                  <div className="px-3 py-2 border-b border-jeket-gold/10">
                    <p className="text-sm font-medium text-jeket-silver-bright truncate">
                      {user.email || user.username}
                    </p>
                    {(() => {
                      const shown = displayRole(user);
                      return (
                        <span
                          className={clsx(
                            'text-xs px-1.5 py-0.5 rounded text-white inline-block mt-1',
                            roleBadgeColors[shown.key] || 'bg-gray-500'
                          )}
                          title={shown.title}
                          data-testid="user-role-badge"
                        >
                          {shown.label}
                        </span>
                      );
                    })()}
                  </div>

                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/org/account');
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-jeket-silver-muted hover:text-jeket-silver-bright hover:bg-jeket-black-surface flex items-center gap-2 transition-colors border-b border-jeket-gold/10"
                  >
                    <User size={14} />
                    Account
                  </button>

                  {/* Platform health lives in the staff console (#1156): a link out, new tab.
                      Shown to whoever corp-console's gate admits (jeket-staff), not
                      to superusers as such: a non-staff superuser would land on NotStaff. */}
                  {user.staff && (
                    <a
                      href={CORP_PLATFORM_HEALTH_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setUserMenuOpen(false)}
                      className="w-full px-3 py-2 text-left text-sm text-jeket-silver-muted hover:text-jeket-silver-bright hover:bg-jeket-black-surface flex items-center gap-2 transition-colors border-b border-jeket-gold/10"
                    >
                      <Activity size={14} />
                      Platform health
                      <ExternalLink size={12} className="ml-auto" aria-hidden="true" />
                    </a>
                  )}

                  {/* Org switcher — superusers, and members of several orgs */}
                  {showOrgSwitcher && organizations && (
                    <div className="border-b border-jeket-gold/10 py-1">
                      <p className="px-3 py-1 text-[10px] font-medium text-jeket-silver-muted uppercase tracking-wider">
                        Organization
                      </p>
                      {isSuperuser && (
                        <button
                          onClick={() => handleOrgSwitch(null)}
                          className={clsx(
                            'w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 transition-colors',
                            !selectedOrgId
                              ? 'text-jeket-gold bg-jeket-gold/10'
                              : 'text-jeket-silver-muted hover:text-jeket-silver-bright hover:bg-jeket-black-surface'
                          )}
                        >
                          All Organizations
                        </button>
                      )}
                      {organizations.map((org: MyOrganization) => (
                        <button
                          key={org.id}
                          onClick={() => handleOrgSwitch(org.id)}
                          className={clsx(
                            'w-full px-3 py-1.5 text-left text-xs flex items-center gap-2 transition-colors',
                            selectedOrgId === org.id
                              ? 'text-jeket-gold bg-jeket-gold/10'
                              : 'text-jeket-silver-muted hover:text-jeket-silver-bright hover:bg-jeket-black-surface'
                          )}
                        >
                          {org.name}
                        </button>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={handleLogout}
                    className="w-full px-3 py-2 text-left text-sm text-jeket-silver-muted hover:text-jeket-error hover:bg-jeket-black-surface flex items-center gap-2 transition-colors"
                  >
                    <LogOut size={14} />
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
